import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { useToast } from "@/shared/hooks/use-toast";
import { FeatureLockOverlay } from "@/shared/components/FeatureLockOverlay";
import { Plus, X } from "lucide-react";
import {
  INITIAL_FORM_DATA,
  PropertyFormData,
} from "./comprehensiveAddPropertyModal/types";
import { BasicInfoTab } from "./comprehensiveAddPropertyModal/BasicInfoTab";
import { LocationTab } from "./comprehensiveAddPropertyModal/LocationTab";
import { DetailsTab } from "./comprehensiveAddPropertyModal/DetailsTab";
import { MediaTab } from "./comprehensiveAddPropertyModal/MediaTab";

interface ComprehensiveAddPropertyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  onPropertyAdded: () => void;
}

// Visual-reference modal — feature is gated behind FeatureLockOverlay and does
// not persist anything. clientId / onPropertyAdded are accepted to keep the
// prop contract stable for callers but are intentionally unused.
export function ComprehensiveAddPropertyModal({
  open,
  onOpenChange,
  onPropertyAdded,
}: ComprehensiveAddPropertyModalProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [facilityImage, setFacilityImage] = useState<string | null>(null);
  const [formData, setFormData] = useState<PropertyFormData>(INITIAL_FORM_DATA);

  const updateFormData = (field: keyof PropertyFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
      // Reset sub_type when main type changes
      ...(field === 'property_type' && { property_sub_type: '' }),
    }));
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_DATA);
    setFacilityImage(null);
  };

  // Local-only preview — feature is locked, so nothing is uploaded to storage.
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: "Invalid file", description: "Please select an image file", variant: "destructive" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "Too large", description: "File size must be less than 5MB", variant: "destructive" });
      return;
    }
    setFacilityImage(URL.createObjectURL(file));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.address.trim() || !formData.property_type) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields (Name, Address, Property Type)",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Feature Locked",
      description: `${formData.name} has been added to the client project.`,
    });

    resetForm();
    onPropertyAdded();
    onOpenChange(false);
  };

  const isFormValid = !!(
    formData.name.trim() &&
    formData.address.trim() &&
    formData.property_type
  );

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleImageUpload}
      />

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-primary" />
              Add New Property
            </DialogTitle>
          </DialogHeader>

          {/* MVP2-282: Form content with feature lock overlay */}
          <div className="relative">
            <form onSubmit={handleSubmit} className="space-y-6">
              <Tabs defaultValue="basic" className="space-y-4">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="basic">Basic Info</TabsTrigger>
                  <TabsTrigger value="location">Location</TabsTrigger>
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="media">Media</TabsTrigger>
                </TabsList>

                <TabsContent value="basic">
                  <BasicInfoTab formData={formData} updateFormData={updateFormData} />
                </TabsContent>

                <TabsContent value="location">
                  <LocationTab formData={formData} updateFormData={updateFormData} />
                </TabsContent>

                <TabsContent value="details">
                  <DetailsTab formData={formData} updateFormData={updateFormData} />
                </TabsContent>

                <TabsContent value="media">
                  <MediaTab
                    facilityImage={facilityImage}
                    onUploadClick={() => fileInputRef.current?.click()}
                    onRemoveImage={() => setFacilityImage(null)}
                  />
                </TabsContent>
              </Tabs>
            </form>

            {/* MVP2-282: Feature lock overlay */}
            <FeatureLockOverlay />
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                resetForm();
                onOpenChange(false);
              }}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!isFormValid}
              className="bg-gradient-to-r from-primary to-primary-glow"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Property
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
